import React, { useEffect, useState, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import Highlighter from 'react-highlight-words';

import { mapResourceName } from '@/shared/services/ObjectDisplayService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import AutoComplete from '../input/select/AutoComplete';
import useResourceTree from '@/shared/hooks/resource/useResourceTree';
import guid from '@/shared/utils/guid';

export const crmTypeFilter = (crmType, item, isCurrent) => {
  if (typeof crmType === 'undefined' || crmType === null) return true;
  if (isCurrent) return true; // Allow if set externally (tasks, existing reg)
  const { crmOthers = false, crmProspects = false, crmCustomers = false, crmOwnCompanies = false } =
    item?.model ?? item ?? {};
  // Crm type enabled
  if (crmType === 0 && crmOthers) return true;
  if (crmType === 2 && crmProspects) return true;
  if (crmType === 3 && crmCustomers) return true;
  if (crmType === 4 && crmOwnCompanies) return true;
  // None selected = All selected
  return !crmOthers && !crmProspects && !crmCustomers && !crmOwnCompanies;
};
export const assignmentFilter = (assignmentTemplateId, item) => {
  if (typeof assignmentTemplateId === 'undefined') return true;
  if (!item.blockedAssignmentTemplates || item.blockedAssignmentTemplates.length === 0) return true;
  return item.blockedAssignmentTemplates.indexOf(assignmentTemplateId || guid.empty) < 0;
};
export const resourceSearch = (item, searchValue) => {
  if (!searchValue) return true;
  if (!item) return false;
  if (!item.search) return false;
  return item.search.toUpperCase().indexOf(searchValue.toUpperCase()) >= 0;
};

export const resourceFilter = (
  item,
  { searchValue, assignmentTemplateId, crmType } = {},
  isCurrent = false,
) => {
  if (!resourceSearch(item, searchValue)) return false;
  if (!crmTypeFilter(crmType, item, isCurrent)) return false;
  return assignmentFilter(assignmentTemplateId, item);
};

const ResourceAutoComplete = ({
  intl,
  items: orgItems,
  onChange,
  emptyLabel,
  disabled,
  value,
  small,
  crmType = null,
  assignmentTemplateId = undefined,
  ...rest
}) => {
  const items = useResourceTree(orgItems, intl);

  const selectedResource = useMemo(
    () => (value && value.id ? items.find(x => x.id === value.id) : null),
    [items, value],
  );

  const renderItem = useCallback(
    (item, searchValue, index) => {
      const showCategory =
        item.level === 1 && (index === 0 || item.categoryId !== items[index - 1].categoryId);
      return (
        <div>
          {showCategory ? (
            <div className="margin-10L">
              {searchValue ? (
                <Highlighter
                  highlightClassName="bold"
                  searchWords={[searchValue]}
                  textToHighlight={item.categoryLabel}
                />
              ) : (
                item.categoryLabel
              )}
            </div>
          ) : null}
          <div
            className={classnames({
              'margin-10L': item.level === 0,
            })}
          >
            {searchValue ? (
              <Highlighter
                highlightClassName="bold"
                searchWords={[searchValue]}
                textToHighlight={item.label}
              />
            ) : (
              item.label
            )}
          </div>
        </div>
      );
    },
    [items],
  );

  const filterFunc = useCallback(
    (item, searchValue) => {
      return resourceFilter(
        item,
        {
          searchValue,
          crmType,
          assignmentTemplateId,
        },
        value && value.id === item.id,
      );
    },
    [crmType, value, assignmentTemplateId],
  );

  return (
    <AutoComplete
      items={items}
      onChange={e => onChange(e ? e.model : null)}
      value={selectedResource}
      mode="edit"
      renderItem={renderItem}
      filterFunc={filterFunc}
      isSmall={small}
      disabled={disabled}
      {...rest}
    />
  );
};

export default ResourceAutoComplete;
